import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface BdmFinancialsData {
  totalInvoiced: number;
  totalCreditGiven: number;
  totalRecovered: number;
  pendingRecovery: number;
  overdueRecovery: number;
  recoveryRate: number;
  activeCreditCustomers: number;
}

export interface CustomerCreditRow {
  customerId: string;
  customerName: string;
  totalInvoiced: number;
  totalRecovered: number;
  pendingRecovery: number;
  invoiceCount: number;
  oldestInvoiceDate?: string;
}

export interface RecentCreditInvoice {
  id: string;
  invoiceNumber: string;
  date: string;
  customerName: string;
  grandTotal: number;
  amountDue: number;
  status: string;
}

export interface BdmFinancialsResponse {
  ok: boolean;
  repId: string;
  financials: BdmFinancialsData;
  customerBreakdown: CustomerCreditRow[];
  recentCreditInvoices: RecentCreditInvoice[];
}

export interface DsrSummary {
  newLeadsCount: number;
  followupsDoneCount: number;
  visitsDoneCount: number;
  quotationsCount: number;
  quotationValue: number;
  ordersCount: number;
  orderValue: number;
}

export interface BdmDsrResponse {
  ok: boolean;
  date: string;
  rep: {
    id: string;
    name: string;
    role: string;
    designation: string;
  };
  summary: DsrSummary;
  breakdowns: {
    leads: any[];
    followups: any[];
    visits: any[];
    quotations: any[];
    orders: any[];
  };
  formattedDsrText: string;
}

export interface TeamDsrRow {
  userId: string;
  name: string;
  role: string;
  designation: string;
  phone?: string;
  newLeads: number;
  followupsDone: number;
  visitsDone: number;
  quotationsCount: number;
  quotationValue: number;
  ordersCount: number;
  orderValue: number;
}

export interface TeamDsrResponse {
  ok: boolean;
  date: string;
  team: TeamDsrRow[];
}

export function useBdmFinancials(userId?: string) {
  return useQuery({
    queryKey: ['bdm-financials', userId || 'mine'],
    queryFn: async () => {
      const params = userId ? { userId } : {};
      const { data } = await api.get<BdmFinancialsResponse>('/api/sales-ops/bdm/financials', { params });
      return data;
    },
    staleTime: 30000,
  });
}

export function useBdmDsr(date?: string, userId?: string) {
  return useQuery({
    queryKey: ['bdm-dsr', date || 'today', userId || 'mine'],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (date) params.date = date;
      if (userId) params.userId = userId;
      const { data } = await api.get<BdmDsrResponse>('/api/sales-ops/bdm/dsr', { params });
      return data;
    },
    staleTime: 30000,
  });
}

export function useTeamDsr(date?: string) {
  return useQuery({
    queryKey: ['team-dsr', date || 'today'],
    queryFn: async () => {
      const params = date ? { date } : {};
      const { data } = await api.get<TeamDsrResponse>('/api/sales-ops/bdm/team-dsr', { params });
      return data;
    },
    staleTime: 30000,
  });
}
